import { releaseCanvas, type CanvasLike } from './canvas';
import type { Corners } from './geometry';
import { Pipeline, type RenderParams } from './pipeline';
import type { EngineRequest, EngineResponse } from './protocol';

type EngineSuccess = Extract<EngineResponse, { ok: true }>;

/** A rendered preview: draw `image` (width × height), then call `release()`. */
export interface PreviewImage {
  readonly image: CanvasImageSource;
  readonly width: number;
  readonly height: number;
  release(): void;
}

/** A preview request replaced by a newer one before it ran; callers simply ignore it. */
export class PreviewSupersededError extends Error {
  constructor() {
    super('A newer preview was requested');
  }
}

/**
 * The image engine used by the Strumenti features and identity documents. One instance per image
 * slot. Runs in a Web Worker where the browser can draw off the main thread (OffscreenCanvas), so
 * the page stays responsive; elsewhere (older browsers, jsdom) the same pipeline runs on the page.
 *
 * Memory stays bounded with full-size phone photos: the caller keeps one source canvas (at most
 * 2000 px in identity documents); the engine sends it to the Worker as a transferred ImageBitmap,
 * not a copy; each render releases its intermediate canvases; a preview's backing store goes back
 * once transferred; and at most one preview is in flight, newer requests replacing queued ones.
 */
export interface ImageEngine {
  /** Sets the image to work on. The caller keeps `source`; the engine never changes it. */
  setSource(source: HTMLCanvasElement): Promise<void>;
  /** May reject with `PreviewSupersededError` when a newer preview replaced this one. */
  renderPreview(params: RenderParams): Promise<PreviewImage>;
  /** The full-size result as a JPEG data URL. */
  exportJpeg(params: RenderParams, quality?: number): Promise<string>;
  /** Proposed document corners in source coordinates, or null. */
  detectCorners(): Promise<Corners | null>;
  dispose(): void;
}

export function createImageEngine(): ImageEngine {
  return canWorkOffMainThread() ? new WorkerEngine() : new InlineEngine();
}

function canWorkOffMainThread(): boolean {
  return (
    typeof Worker !== 'undefined' &&
    typeof OffscreenCanvas !== 'undefined' &&
    typeof createImageBitmap !== 'undefined'
  );
}

const DEFAULT_JPEG_QUALITY = 0.92;

/** The pipeline on the page's main thread. */
class InlineEngine implements ImageEngine {
  private readonly pipeline = new Pipeline();

  setSource(source: HTMLCanvasElement): Promise<void> {
    this.pipeline.setSource(source, false);
    return Promise.resolve();
  }

  renderPreview(params: RenderParams): Promise<PreviewImage> {
    try {
      return Promise.resolve(previewOf(this.pipeline.render(params)));
    } catch (error) {
      return Promise.reject(asError(error));
    }
  }

  exportJpeg(params: RenderParams, quality = DEFAULT_JPEG_QUALITY): Promise<string> {
    try {
      const canvas = this.pipeline.render(params) as HTMLCanvasElement;
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      releaseCanvas(canvas);
      return Promise.resolve(dataUrl);
    } catch (error) {
      return Promise.reject(asError(error));
    }
  }

  detectCorners(): Promise<Corners | null> {
    return Promise.resolve(this.pipeline.detectCorners());
  }

  dispose(): void {
    this.pipeline.clear();
  }
}

function previewOf(canvas: CanvasLike): PreviewImage {
  return {
    image: canvas,
    width: canvas.width,
    height: canvas.height,
    release: () => releaseCanvas(canvas),
  };
}

interface QueuedPreview {
  readonly params: RenderParams;
  readonly resolve: (preview: PreviewImage) => void;
  readonly reject: (error: Error) => void;
}

/**
 * The pipeline in a module Worker. If the Worker fails — it cannot start, or it reports an error
 * because the browser lacks something it needs — the engine switches to the inline pipeline with
 * the last source and retries, so the page keeps working, only on the main thread.
 */
class WorkerEngine implements ImageEngine {
  private worker: Worker | null;
  private fallback: InlineEngine | null = null;
  private lastSource: HTMLCanvasElement | null = null;
  /** Settles once the latest source has reached the Worker; every other request waits for it. */
  private sourceSent: Promise<void> = Promise.resolve();
  private previewInFlight = false;
  private queuedPreview: QueuedPreview | null = null;
  private nextId = 1;
  private readonly pending = new Map<
    number,
    { resolve: (r: EngineSuccess) => void; reject: (e: Error) => void }
  >();

  constructor() {
    this.worker = new Worker(new URL('./image-engine.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (event: MessageEvent<EngineResponse>) => {
      const response = event.data;
      if (!response.ok) {
        this.failOver();
        return;
      }
      const entry = this.pending.get(response.id);
      if (!entry) return;
      this.pending.delete(response.id);
      entry.resolve(response);
    };
    this.worker.onerror = () => this.failOver();
  }

  setSource(source: HTMLCanvasElement): Promise<void> {
    this.lastSource = source;
    // Chained, so two quick image changes reach the Worker in order.
    this.sourceSent = this.sourceSent
      .catch(() => undefined)
      .then(async () => {
        const before = this.inline();
        if (before) return before.setSource(source);
        const bitmap = await createImageBitmap(source);
        await this.call((id) => ({ id, kind: 'source', bitmap }), [bitmap]);
        const after = this.inline();
        if (after) await after.setSource(source);
      });
    return this.sourceSent;
  }

  renderPreview(params: RenderParams): Promise<PreviewImage> {
    return new Promise<PreviewImage>((resolve, reject) => {
      if (this.previewInFlight) {
        this.queuedPreview?.reject(new PreviewSupersededError());
        this.queuedPreview = { params, resolve, reject };
        return;
      }
      this.runPreview({ params, resolve, reject });
    });
  }

  async exportJpeg(params: RenderParams, quality = DEFAULT_JPEG_QUALITY): Promise<string> {
    await this.sourceSent.catch(() => undefined);
    const before = this.inline();
    if (before) return before.exportJpeg(params, quality);
    const response = await this.call((id) => ({ id, kind: 'export', params, quality }));
    const after = this.inline();
    if (after) return after.exportJpeg(params, quality);
    if (!response?.blob) throw new Error('The image engine returned no image');
    return blobToDataUrl(response.blob);
  }

  async detectCorners(): Promise<Corners | null> {
    await this.sourceSent.catch(() => undefined);
    const before = this.inline();
    if (before) return before.detectCorners();
    const response = await this.call((id) => ({ id, kind: 'detect' }));
    const after = this.inline();
    if (after) return after.detectCorners();
    return response?.corners ?? null;
  }

  dispose(): void {
    this.worker?.terminate();
    this.worker = null;
    this.fallback?.dispose();
    this.lastSource = null;
    this.queuedPreview?.reject(new Error('The image engine was closed'));
    this.queuedPreview = null;
    for (const entry of this.pending.values())
      entry.reject(new Error('The image engine was closed'));
    this.pending.clear();
  }

  private runPreview(request: QueuedPreview): void {
    this.previewInFlight = true;
    this.previewOnce(request.params)
      .then(request.resolve, (error: unknown) => request.reject(asError(error)))
      .finally(() => {
        this.previewInFlight = false;
        const next = this.queuedPreview;
        this.queuedPreview = null;
        if (next) this.runPreview(next);
      });
  }

  private async previewOnce(params: RenderParams): Promise<PreviewImage> {
    await this.sourceSent.catch(() => undefined);
    const before = this.inline();
    if (before) return before.renderPreview(params);
    const response = await this.call((id) => ({ id, kind: 'preview', params }));
    const after = this.inline();
    if (after) return after.renderPreview(params);
    const bitmap = response?.bitmap;
    if (!bitmap) throw new Error('The image engine returned no preview');
    return {
      image: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      release: () => bitmap.close(),
    };
  }

  /** Resolves with the response, or with null once the engine has failed over to the page. */
  private call(
    build: (id: number) => EngineRequest,
    transfer: Transferable[] = [],
  ): Promise<EngineSuccess | null> {
    const worker = this.worker;
    if (!worker) return Promise.resolve(null);
    const id = this.nextId++;
    return new Promise<EngineSuccess | null>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      try {
        worker.postMessage(build(id), transfer);
      } catch (error) {
        this.pending.delete(id);
        reject(asError(error));
      }
    }).catch((error: unknown) => {
      if (this.inline()) return null;
      throw error;
    });
  }

  /** The inline engine once the Worker has failed (read through a method: it changes across awaits). */
  private inline(): InlineEngine | null {
    return this.fallback;
  }

  private failOver(): void {
    if (this.fallback) return;
    this.worker?.terminate();
    this.worker = null;
    this.fallback = new InlineEngine();
    if (this.lastSource) void this.fallback.setSource(this.lastSource);
    for (const entry of this.pending.values()) entry.reject(new Error('worker failed'));
    this.pending.clear();
  }
}

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === 'string'
        ? resolve(reader.result)
        : reject(new Error('Could not read the image'));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the image'));
    reader.readAsDataURL(blob);
  });
}
